/**
 * Snapshot migration, the pure half (issue #17; CONTEXT.md, "Snapshot migration"). When the app
 * adopts a new Snapshot, every UUID a Character names is sent through Foundry's uuid-redirects
 * (the redirect pass), then whatever the new Snapshot still lacks is dealt with (the resolution
 * pass): a Selection the user made reopens as an open Choice carrying a warning that names the
 * old Entry; a spell, an inventory item or a Session item is dropped and reported. Granted
 * Selections are the engine's: the reconciler replaces or removes them when the Character
 * settles (../character/grants.ts), and ./index.ts reports what it removed.
 *
 * Nothing here reads the Snapshot: the target says what it has and what things are called.
 */
import type { RedirectMap } from "../../shared/snapshot-format";
import type { AnswerValue, Character, Choice, ChoiceSource, InventoryItem, KnownSpell, LevelNumber, MigrationRemoved, MigrationReopened, Reopened, SpellcastingState } from "../character/model";
import { choiceKey, isGranted } from "../character/model";
import { slotLabel } from "../guide/steps";
import type { ConsumableStack, Session, SessionCondition, SessionEffect } from "../session/model";

/** What a migration knows about the Snapshot the Character moves to, and the one it leaves. */
export interface MigrationTarget {
  /** The Snapshot tag the Character moves to (`manifest.snapshot.release`). */
  to: string;
  /** Old UUID to current UUID, as the new Snapshot ships it (`redirects.json`). */
  redirects: RedirectMap;
  /** Whether the new Snapshot ships an Entry with this UUID. */
  has(uuid: string): boolean;
  /** The name of an Entry the new Snapshot ships; null when it does not. */
  nameOf(uuid: string): string | null;
  /** The name an Entry had in the previous Snapshot, when its index is still at hand (the update hook of #3 passes it); null otherwise. */
  oldNameOf(uuid: string): string | null;
}

export const MAX_REDIRECT_HOPS = 8;

/** Follow a redirect chain (A to B, B to C gives C). A UUID with no redirect is returned as it is. */
export function followRedirects(map: RedirectMap, uuid: string): string {
  let current = uuid;
  for (let i = 0; i < MAX_REDIRECT_HOPS && map[current]; i++) current = map[current]!;
  return current;
}

/** Whether a stored value is a Foundry compendium UUID (the only kind a Selection or an Answer holds). */
export function isUuid(value: unknown): value is string {
  return typeof value === "string" && value.startsWith("Compendium.");
}

export interface ResolvedCharacter {
  character: Character;
  /** How many UUIDs the redirects replaced. */
  redirected: number;
  reopened: MigrationReopened[];
  removed: MigrationRemoved[];
  /** Whether anything at all changed (the returned Character is a copy either way). */
  changed: boolean;
}

const FOUNDATION_LABELS: Record<Extract<ChoiceSource, { kind: "ancestry" | "heritage" | "background" | "class" | "dual-class" }>["kind"], string> = {
  ancestry: "Ancestry",
  heritage: "Heritage",
  background: "Background",
  class: "Class",
  "dual-class": "Second class (Dual Class)",
};

function labelOf(choice: Choice, target: MigrationTarget): string {
  const source = choice.source;
  switch (source.kind) {
    case "progression":
      return slotLabel(source.slot);
    case "rule-element":
    case "feature":
      return `Granted by ${target.nameOf(source.uuid) ?? target.oldNameOf(source.uuid) ?? source.uuid}`;
    default:
      return FOUNDATION_LABELS[source.kind];
  }
}

/**
 * The redirect pass and the resolution pass over one Character. The returned Character still
 * carries its old `snapshotTag`; ./index.ts settles it, runs the re-evaluation pass and records
 * the migration. Pure: the input is never touched.
 */
export function redirectAndResolve(input: Character, target: MigrationTarget): ResolvedCharacter {
  const character = structuredClone(input);
  const from = input.snapshotTag;
  const reopened: MigrationReopened[] = [];
  const removed: MigrationRemoved[] = [];
  let redirected = 0;
  const redirect = (uuid: string): string => {
    const next = followRedirects(target.redirects, uuid);
    if (next !== uuid) redirected++;
    return next;
  };
  const record = (flag: string | null, uuid: string | null): Reopened => ({ from, to: target.to, flag, uuid, name: uuid ? (target.oldNameOf(uuid) ?? target.nameOf(uuid)) : null, reason: "missing", detail: null });

  /* Selections, granting Entries and Answers. */
  for (const level of character.levels) {
    for (const choice of level.choices) {
      if (isGranted(choice.source)) {
        const next = redirect(choice.source.uuid);
        if (next !== choice.source.uuid) choice.source = { ...choice.source, uuid: next };
      }
      const key = choiceKey(level.number, choice.source);
      const userOwned = !isGranted(choice.source) || choice.detached === true;
      let selectionReopened = false;
      if (choice.selection) {
        const before = choice.selection.uuid;
        const next = redirect(before);
        if (next !== before) choice.selection = { uuid: next, snapshotTag: target.to };
        if (!target.has(next) && userOwned) {
          // The user's Entry is gone and nothing succeeds it: the Choice reopens with the warning.
          const r = record(null, before);
          delete choice.selection;
          delete choice.answers;
          choice.reopened = [r];
          reopened.push({ ...r, key, level: level.number, label: labelOf(choice, target) });
          selectionReopened = true;
        }
        // A granted Selection the new Snapshot lacks stays for the reconciler, which replaces it with what its granter now names or removes it.
      }
      if (choice.answers && !selectionReopened) {
        const answers: Record<string, AnswerValue> = {};
        const warnings: Reopened[] = choice.reopened ? [...choice.reopened] : [];
        let touched = false;
        for (const [flag, value] of Object.entries(choice.answers)) {
          const r = redirectAnswer(value, target, redirect);
          if (r.missing !== null) {
            // The Answer named an Entry that is gone: the prompt reopens on the Choice.
            const rec = record(flag, r.missing);
            warnings.push(rec);
            reopened.push({ ...rec, key, level: level.number, label: `${labelOf(choice, target)}: ${flag}` });
            touched = true;
            continue;
          }
          answers[flag] = r.value;
        }
        if (touched) {
          choice.answers = answers;
          choice.reopened = warnings;
        } else choice.answers = answers;
      }
    }
  }

  /* Spells the user gave each Spellcasting Entry. */
  for (const [entryId, state] of Object.entries(character.spellcasting.entries)) {
    character.spellcasting.entries[entryId] = resolveSpellcasting(state, entryId, target, redirect, removed);
  }

  /* The inventory. */
  const dropped = new Set<string>();
  const items: InventoryItem[] = [];
  for (const item of character.inventory.items) {
    const next = redirect(item.uuid);
    if (!target.has(next)) {
      dropped.add(item.id);
      removed.push({ kind: "item", uuid: item.uuid, name: target.oldNameOf(item.uuid), detail: item.quantity > 1 ? `×${item.quantity}` : null });
      continue;
    }
    items.push(next === item.uuid ? item : { ...item, uuid: next });
  }
  for (const item of items) {
    if (item.container && dropped.has(item.container)) delete item.container;
    if (item.ammo && dropped.has(item.ammo)) delete item.ammo;
  }
  character.inventory.items = items;

  /* The Session, when an imported Character carries one (the runner never migrates under an open Session). */
  if (character.session) character.session = resolveSession(character.session, target, redirect, removed);

  return { character, redirected, reopened, removed, changed: redirected > 0 || reopened.length > 0 || removed.length > 0 };
}

/** Redirect the UUIDs inside one Answer value. `missing` names the first UUID the new Snapshot lacks, in which case the Answer cannot stand. */
function redirectAnswer(value: AnswerValue, target: MigrationTarget, redirect: (uuid: string) => string): { value: AnswerValue; missing: string | null } {
  if (isUuid(value)) {
    const next = redirect(value);
    return target.has(next) ? { value: next, missing: null } : { value, missing: value };
  }
  if (typeof value === "object" && value !== null) {
    const out: { [key: string]: AnswerValue } = {};
    for (const [k, v] of Object.entries(value)) {
      const r = redirectAnswer(v, target, redirect);
      if (r.missing !== null) return { value, missing: r.missing };
      out[k] = r.value;
    }
    return { value: out, missing: null };
  }
  return { value, missing: null };
}

function resolveSpellcasting(state: SpellcastingState, entryId: string, target: MigrationTarget, redirect: (uuid: string) => string, removed: MigrationRemoved[]): SpellcastingState {
  const reported = new Set<string>();
  const report = (uuid: string, where: string): void => {
    if (reported.has(uuid)) return;
    reported.add(uuid);
    removed.push({ kind: "spell", uuid, name: target.oldNameOf(uuid), detail: where });
  };
  const known: KnownSpell[] = [];
  for (const spell of state.known) {
    const next = redirect(spell.uuid);
    if (!target.has(next)) {
      report(spell.uuid, entryId);
      continue;
    }
    known.push(next === spell.uuid ? spell : { ...spell, uuid: next });
  }
  const prepared: Record<string, (string | null)[]> = {};
  for (const [rank, slots] of Object.entries(state.prepared)) {
    prepared[rank] = slots.map((uuid) => {
      if (uuid === null) return null;
      const next = redirect(uuid);
      if (target.has(next)) return next;
      report(uuid, `${entryId}, prepared at rank ${rank}`);
      return null;
    });
  }
  return { known, prepared };
}

function resolveSession(session: Session, target: MigrationTarget, redirect: (uuid: string) => string, removed: MigrationRemoved[]): Session {
  const keep = <T extends SessionCondition | SessionEffect | ConsumableStack>(item: T, kind: MigrationRemoved["kind"]): T | null => {
    const next = redirect(item.uuid);
    if (!target.has(next)) {
      removed.push({ kind, uuid: item.uuid, name: item.name, detail: null });
      return null;
    }
    return next === item.uuid ? item : { ...item, uuid: next };
  };
  const conditions = session.conditions.map((c) => keep(c, "condition")).filter((c): c is SessionCondition => c !== null);
  const effects = session.effects.map((e) => keep(e, "effect")).filter((e): e is SessionEffect => e !== null);
  const consumables = session.consumables.map((s) => keep(s, "consumable")).filter((s): s is ConsumableStack => s !== null);
  // Counts keyed by Entry UUID follow their Entry; those of Entries that are gone go with them.
  const rekey = (counts: Record<string, number>): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const [uuid, n] of Object.entries(counts)) {
      const next = redirect(uuid);
      if (!target.has(next)) continue;
      out[next] = (out[next] ?? 0) + n;
    }
    return out;
  };
  const spellcasting: Session["spellcasting"] = {};
  for (const [id, use] of Object.entries(session.spellcasting)) spellcasting[id] = { ...use, innate: rekey(use.innate) };
  return { ...session, conditions, effects, consumables, uses: rekey(session.uses), spellcasting };
}

/** The granted (non-detached) Selections a Character holds, by Choice key: what the settle after the redirect pass may replace or remove. */
export function grantedSelections(character: Character): Map<string, { uuid: string; level: LevelNumber }> {
  const out = new Map<string, { uuid: string; level: LevelNumber }>();
  for (const level of character.levels) {
    for (const choice of level.choices) {
      if (isGranted(choice.source) && !choice.detached && choice.selection) out.set(choiceKey(level.number, choice.source), { uuid: choice.selection.uuid, level: level.number });
    }
  }
  return out;
}
